import React, { useState } from "react";
import { apiClient } from "@/lib/api";
import { toast } from "sonner";

export default function SocialShare({ slug, petName, variant = "row", title = "Share" }) {
  const [open, setOpen] = useState(variant === "row");
  // Use the /api/s/{slug} share endpoint — it serves per-memorial Open Graph
  // tags for rich social previews (Facebook/Twitter/iMessage/WhatsApp) and
  // instantly redirects humans to the SPA memorial page.
  const backend = (process.env.REACT_APP_BACKEND_URL || window.location.origin).replace(/\/+$/, "");
  const url = `${backend}/api/s/${slug}`;
  const text = `In loving memory of ${petName} — a memorial at The Heirloom Pet.`;
  const fullText = `${text} ${url}`;

  const track = (channel) => {
    apiClient.post(`/memorials/${slug}/track`, { event: "share_click", channel }).catch(() => {});
  };

  const links = [
    {
      name: "facebook",
      label: "Facebook",
      href: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}&quote=${encodeURIComponent(text)}`,
      svg: (
        <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor"><path d="M22 12a10 10 0 1 0-11.56 9.88v-6.99H7.9V12h2.54V9.8c0-2.51 1.49-3.89 3.78-3.89 1.1 0 2.24.2 2.24.2v2.46h-1.26c-1.24 0-1.63.77-1.63 1.57V12h2.78l-.44 2.89h-2.34v6.99A10 10 0 0 0 22 12z"/></svg>
      ),
    },
    {
      name: "instagram",
      label: "Instagram",
      href: "https://www.instagram.com",
      newWindow: true,
      onClickExtra: async () => {
        try {
          await navigator.clipboard.writeText(`${text} ${url}`);
          toast.success("Link copied ✦ paste it in your Story or caption on Instagram.", { duration: 5000 });
        } catch {
          toast("Open Instagram and paste the memorial link in your Story.");
        }
      },
      svg: (
        <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor"><path d="M12 2.16c3.2 0 3.58.01 4.85.07 1.17.05 1.8.25 2.23.41.56.22.96.48 1.38.9.42.42.68.82.9 1.38.16.42.36 1.06.41 2.23.06 1.27.07 1.65.07 4.85s-.01 3.58-.07 4.85c-.05 1.17-.25 1.8-.41 2.23-.22.56-.48.96-.9 1.38-.42.42-.82.68-1.38.9-.42.16-1.06.36-2.23.41-1.27.06-1.65.07-4.85.07s-3.58-.01-4.85-.07c-1.17-.05-1.8-.25-2.23-.41-.56-.22-.96-.48-1.38-.9-.42-.42-.68-.82-.9-1.38-.16-.42-.36-1.06-.41-2.23-.06-1.27-.07-1.65-.07-4.85s.01-3.58.07-4.85c.05-1.17.25-1.8.41-2.23.22-.56.48-.96.9-1.38.42-.42.82-.68 1.38-.9.42-.16 1.06-.36 2.23-.41C8.42 2.17 8.8 2.16 12 2.16zM12 0C8.74 0 8.33.01 7.05.07 5.78.13 4.9.33 4.14.63c-.79.31-1.46.72-2.13 1.38C1.35 2.68.94 3.35.63 4.14c-.3.76-.5 1.64-.56 2.91C.01 8.33 0 8.74 0 12s.01 3.67.07 4.95c.06 1.27.26 2.15.56 2.91.31.79.72 1.46 1.38 2.13.67.67 1.34 1.08 2.13 1.38.76.3 1.64.5 2.91.56C8.33 23.99 8.74 24 12 24s3.67-.01 4.95-.07c1.27-.06 2.15-.26 2.91-.56.79-.31 1.46-.72 2.13-1.38.67-.67 1.08-1.34 1.38-2.13.3-.76.5-1.64.56-2.91.06-1.28.07-1.69.07-4.95s-.01-3.67-.07-4.95c-.06-1.27-.26-2.15-.56-2.91-.31-.79-.72-1.46-1.38-2.13C21.32 1.35 20.65.94 19.86.63c-.76-.3-1.64-.5-2.91-.56C15.67.01 15.26 0 12 0zm0 5.84a6.16 6.16 0 1 0 0 12.32 6.16 6.16 0 0 0 0-12.32zm0 10.16a4 4 0 1 1 0-8 4 4 0 0 1 0 8zm6.4-11.85a1.44 1.44 0 1 0 0 2.88 1.44 1.44 0 0 0 0-2.88z"/></svg>
      ),
    },
    {
      name: "tiktok",
      label: "TikTok",
      href: "https://www.tiktok.com",
      newWindow: true,
      onClickExtra: async () => {
        try {
          await navigator.clipboard.writeText(`${text} ${url}`);
          toast.success("Link copied ✦ paste it in your TikTok caption or bio.", { duration: 5000 });
        } catch {
          toast("Open TikTok and paste the memorial link in your video caption.");
        }
      },
      svg: (
        <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor"><path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-5.86 11.81 6.84 6.84 0 0 0 11.66-4.83V8.45a8.16 8.16 0 0 0 4.77 1.52V6.55a4.85 4.85 0 0 1-1.34.14z"/></svg>
      ),
    },
    {
      name: "twitter",
      label: "X / Twitter",
      href: `https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(url)}`,
      svg: (
        <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor"><path d="M18.244 2H21.5l-7.5 8.57L23 22h-6.84l-5.36-7-6.13 7H1.5l8.02-9.17L1 2h6.96l4.85 6.43L18.244 2zm-2.4 18h1.89L8.16 4H6.18l9.66 16z"/></svg>
      ),
    },
    {
      name: "pinterest",
      label: "Pinterest",
      href: `https://pinterest.com/pin/create/button/?url=${encodeURIComponent(url)}&description=${encodeURIComponent(text)}`,
      svg: (
        <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor"><path d="M12 2A10 10 0 0 0 8.36 21.32c-.08-.78-.16-2 .03-2.87.18-.78 1.14-4.96 1.14-4.96s-.29-.58-.29-1.43c0-1.34.78-2.34 1.75-2.34.82 0 1.22.62 1.22 1.36 0 .83-.53 2.08-.8 3.23-.23.96.49 1.75 1.44 1.75 1.73 0 3.06-1.83 3.06-4.47 0-2.34-1.68-3.97-4.07-3.97-2.77 0-4.4 2.08-4.4 4.23 0 .84.32 1.74.73 2.23.08.1.09.18.07.28-.08.34-.27 1.1-.31 1.25-.05.2-.16.25-.36.15-1.34-.62-2.18-2.58-2.18-4.15 0-3.38 2.45-6.48 7.07-6.48 3.71 0 6.6 2.65 6.6 6.18 0 3.69-2.32 6.66-5.55 6.66-1.08 0-2.1-.56-2.45-1.23l-.66 2.54c-.24.93-.89 2.1-1.32 2.81A10 10 0 1 0 12 2z"/></svg>
      ),
    },
    {
      name: "whatsapp",
      label: "WhatsApp",
      href: `https://api.whatsapp.com/send?text=${encodeURIComponent(fullText)}`,
      svg: (
        <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor"><path d="M.057 24l1.687-6.163a11.867 11.867 0 0 1-1.587-5.946C.16 5.335 5.495 0 12.05 0a11.82 11.82 0 0 1 8.413 3.488 11.823 11.823 0 0 1 3.483 8.414c-.003 6.555-5.338 11.892-11.893 11.892a11.9 11.9 0 0 1-5.688-1.448L.057 24zm6.597-3.807c1.676.995 3.276 1.591 5.392 1.592 5.448 0 9.886-4.434 9.889-9.885.002-5.462-4.415-9.89-9.881-9.892-5.452 0-9.887 4.434-9.889 9.884a9.86 9.86 0 0 0 1.515 5.273l-.999 3.648 3.973-.62zm11.387-5.464c-.074-.124-.272-.198-.57-.347-.297-.149-1.758-.868-2.031-.967-.272-.099-.47-.149-.669.149-.198.297-.768.967-.941 1.165-.173.198-.347.223-.644.074-.297-.149-1.255-.462-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.297-.347.446-.521.151-.172.2-.296.3-.495.099-.198.05-.372-.025-.521-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51l-.57-.01c-.198 0-.52.074-.792.372s-1.04 1.016-1.04 2.479 1.065 2.876 1.213 3.074c.149.198 2.095 3.2 5.076 4.487.709.306 1.263.489 1.694.626.712.226 1.36.194 1.872.118.571-.085 1.758-.719 2.006-1.413.247-.694.247-1.289.173-1.413z"/></svg>
      ),
    },
    {
      name: "email",
      label: "Email",
      href: `mailto:?subject=${encodeURIComponent(`A memorial for ${petName}`)}&body=${encodeURIComponent(fullText)}`,
      svg: (
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="5" width="18" height="14" rx="2"/><path d="m3 7 9 6 9-6"/></svg>
      ),
    },
  ];

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(url);
      track("copy_link");
      toast.success("Link copied to share ✦");
    } catch {
      toast.error("Could not copy.");
    }
  };

  const handleClick = (link) => {
    track(link.name);
    if (link.onClickExtra) link.onClickExtra();
  };

  if (!open) {
    return (
      <button onClick={() => setOpen(true)} className="btn-ghost" data-testid="share-toggle-btn">
        {title}
      </button>
    );
  }

  return (
    <div className="flex flex-wrap items-center justify-center gap-2" data-testid="social-share-row">
      {links.map((l) => (
        <a
          key={l.name}
          href={l.href}
          target="_blank"
          rel="noopener noreferrer"
          onClick={() => handleClick(l.name)}
          title={l.label}
          aria-label={`Share on ${l.label}`}
          data-testid={`share-${l.name}`}
          className="w-11 h-11 rounded-full border border-[var(--rb-gold)]/40 bg-white/70 backdrop-blur text-[var(--rb-text-heading)] hover:bg-[var(--rb-gold)] hover:text-white hover:border-[var(--rb-gold)] flex items-center justify-center transition-all duration-300"
        >
          {l.svg}
        </a>
      ))}
      <button
        onClick={copy}
        title="Copy link"
        aria-label="Copy link"
        data-testid="share-copy"
        className="w-11 h-11 rounded-full border border-[var(--rb-gold)]/40 bg-white/70 backdrop-blur text-[var(--rb-text-heading)] hover:bg-[var(--rb-gold)] hover:text-white hover:border-[var(--rb-gold)] flex items-center justify-center transition-all duration-300"
      >
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"/><path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.72-1.71"/></svg>
      </button>
    </div>
  );
}
